import React from "react";
import { RxCross1 } from "react-icons/rx";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { toast } from "react-toastify";
import Tooltip from "../../../../Components/Tooltip/Tooltip";
import Toggle from "../../../../Components/Common/Toggle";
import SelectDropdown from "../../../../Components/Dropdown/SelectDropdown";
import { DocApi } from "../../../../App/api/document.apis";

function DocumentModel({
  isdocModel,
  handleDocumentDelete,
  fetchAllData,
  handlecloseDocModel,
  editData = null,
  userRoleData = [],
}) {
  const initialValues = {
    _id: editData?._id || "",
    name: editData?.name || "",
    userRole: editData?.userRole?._id || "", // single select → store _id
    is_mandatory: editData?.is_mandatory ?? false,
    is_visible: editData?.is_visible ?? false,
    is_expiry_date: editData?.is_expiry_date ?? false,
    is_unique_code: editData?.is_unique_code ?? false,
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      ...values,
      user_role: values.userRole, // backend expects user_role
    };

    try {
      if (editData) {
        await DocApi.updateDocument(payload, initialValues._id);
        toast.success("Document updated successfully");
      } else {
        await DocApi.createDocument(payload);
        toast.success("Document created successfully");
      }

      fetchAllData();
      resetForm();
      handlecloseDocModel();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save document");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isdocModel) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={handlecloseDocModel}
      />

      {/* Right-side Slide-in Modal */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50
          transform transition-transform duration-300 ease-in-out
          ${isdocModel ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {editData ? "Edit Document" : "Add New Document"}
            </h2>
            <button
              onClick={handlecloseDocModel}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <RxCross1 className="text-gray-600 text-xl" />
            </button>
          </div>

          {/* Form Content */}
          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {({ values, setFieldValue, isSubmitting }) => (
              <Form className="flex-1 flex flex-col px-6 py-6 overflow-y-auto">
                {/* Document Name */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
                    Document Name
                    <Tooltip
                      text="Enter document name (e.g. Aadhaar Card, PAN)"
                      size={18}
                    />
                  </label>
                  <Field
                    type="text"
                    name="name"
                    placeholder="Enter document name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-red-600 text-sm mt-1.5"
                  />
                </div>

                {/* Select Type (User Role) */}
                <div className="mb-8">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
                    Select Type
                    <Tooltip
                      text="Choose which user role this document applies to"
                      size={18}
                    />
                  </label>
                  <select
                    name="userRole"
                    value={values.userRole || ""}
                    onChange={(e) => setFieldValue("userRole", e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  >
                    <option value="" disabled>
                      Select user role / type
                    </option>

                    {userRoleData?.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>

                  <ErrorMessage
                    name="userRole"
                    component="div"
                    className="text-red-600 text-sm mt-1.5"
                  />
                </div>

                {/* Toggles */}
                <div className="space-y-5 mb-8">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">
                    Document Settings
                  </h3>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">
                      Is this document mandatory?
                    </span>
                    <Toggle
                      value={values.is_mandatory}
                      onChange={() =>
                        setFieldValue("is_mandatory", !values.is_mandatory)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">
                      Document visible to users?
                    </span>
                    <Toggle
                      value={values.is_visible}
                      onChange={() =>
                        setFieldValue("is_visible", !values.is_visible)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">
                      Does document have expiry date?
                    </span>
                    <Toggle
                      value={values.is_expiry_date}
                      onChange={() =>
                        setFieldValue("is_expiry_date", !values.is_expiry_date)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">
                      Does document have unique code?
                    </span>
                    <Toggle
                      value={values.is_unique_code}
                      onChange={() =>
                        setFieldValue("is_unique_code", !values.is_unique_code)
                      }
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="mt-auto pt-6 border-t border-gray-200 flex gap-4">
                  <button
                    type="button"
                    onClick={handleDocumentDelete}
                    disabled={isSubmitting}
                    className="flex-1 py-3 px-6 border bg-red-500 rounded-lg font-medium text-white hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`
                      flex-1 py-3 px-6 rounded-lg font-medium text-white
                      ${
                        isSubmitting
                          ? "bg-purple-400 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700"
                      }
                      transition-colors
                    `}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : editData
                      ? "Update"
                      : "Create"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}

export default DocumentModel;
